import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import http from "../../../api/apiClient";
import type { SchoolCalendarEvent, SchoolYear } from "../../../pages/schoolCalendar/types";
import {
  getSchoolYearDateRange,
  isSchoolYearInDateRange,
  normalizeList,
  schoolYearLabel,
} from "../../../pages/schoolCalendar/utils";
import { buildYearPlannerMonths } from "../../../pages/schoolCalendar/yearPlannerMonths";
import { exportYearPlannerToExcel } from "../../../pages/schoolCalendar/yearPlannerExcelExport";

const toDateParam = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Jaarplanning als Excel voor ouders.
 * Hergebruikt de Yearplanner-export (zelfde maandmodel en bestandsformaat), maar met uitsluitend
 * de agenda-items die de ouder al via het ouderportaal mag zien (/parent-portal/calendar/events).
 * Er is daardoor geen Yearplanner-recht nodig.
 */
export const downloadParentYearPlannerExcel = async (language?: string) => {
  const { data: yearsData } = await http.get("/years");
  const schoolYear = normalizeList<SchoolYear>(yearsData).find((year) => isSchoolYearInDateRange(year));
  const { startDate, endDate } = getSchoolYearDateRange(schoolYear);
  if (!schoolYear || !startDate || !endDate) {
    throw new Error("No current school year available");
  }

  const { data: eventsData } = await http.get("/parent-portal/calendar/events", {
    params: {
      from: `${toDateParam(startDate)}T00:00:00`,
      to: `${toDateParam(endDate)}T23:59:59`,
      published: true,
    },
  });

  const yearPlannerMonths = buildYearPlannerMonths({
    events: normalizeList<SchoolCalendarEvent>(eventsData),
    schoolYear,
    language,
  });

  await exportYearPlannerToExcel({ yearPlannerMonths, schoolYear: schoolYearLabel(schoolYear) });
};

export const useParentYearPlannerExcel = () => {
  const { i18n } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);
  const busyRef = useRef(false);

  const download = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setDownloading(true);
    setFailed(false);
    try {
      await downloadParentYearPlannerExcel(i18n.language);
    } catch {
      setFailed(true);
    } finally {
      busyRef.current = false;
      setDownloading(false);
    }
  }, [i18n.language]);

  return { download, downloading, failed };
};
